import * as Sentry from "@sentry/react";

/**
 * React owns the DOM under `#root`, but browser translation (Chrome's
 * translate rewrites text nodes and wraps them in `<font>`), some extensions
 * and in-app browsers mutate it behind React's back. The next commit then
 * calls `removeChild`/`insertBefore` with a reference node that is no longer
 * where React left it, the browser throws `NotFoundError`, and the error
 * boundary replaces the whole screen with the fallback — for the sign-in
 * screen that means the user cannot get in at all.
 *
 * This is the long-standing mitigation from facebook/react#11538: when the
 * parent no longer matches, skip the operation instead of throwing. React's
 * view of the tree is slightly off for that node, which is harmless compared
 * to a crashed screen. Every skip leaves a breadcrumb, and the first one per
 * page load is captured as a warning so the frequency stays visible.
 */

interface ChildNodeLike {
  parentNode: unknown;
}

export interface NodePrototypeLike {
  removeChild(child: ChildNodeLike): unknown;
  insertBefore(
    newChild: unknown,
    referenceChild: ChildNodeLike | null,
  ): unknown;
  appendChild?(newChild: unknown): unknown;
}

type ForeignMutationOperation = "removeChild" | "insertBefore";

let didCaptureForeignMutation = false;

const noteForeignMutation = (operation: ForeignMutationOperation): void => {
  Sentry.addBreadcrumb({
    category: "dom.foreign_mutation",
    level: "warning",
    message: `Skipped ${operation}: node is no longer a child of its React parent`,
  });
  if (didCaptureForeignMutation) {
    return;
  }
  didCaptureForeignMutation = true;
  Sentry.captureMessage(
    `DOM mutated outside React (${operation} skipped)`,
    "warning",
  );
};

/** Test seam: forget that a warning was already captured this page load. */
export const resetDomResilienceForTests = (): void => {
  didCaptureForeignMutation = false;
};

/**
 * Patches `removeChild` and `insertBefore` on the given prototype (normally
 * `Node.prototype`). Idempotent per prototype.
 */
export const installDomResilience = (prototype: NodePrototypeLike): void => {
  const marker = prototype as NodePrototypeLike & {
    __xcfDomResilienceInstalled?: boolean;
  };
  if (marker.__xcfDomResilienceInstalled) {
    return;
  }
  marker.__xcfDomResilienceInstalled = true;

  const originalRemoveChild = prototype.removeChild;
  prototype.removeChild = function (this: unknown, child: ChildNodeLike) {
    if (child && child.parentNode !== this) {
      noteForeignMutation("removeChild");
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = prototype.insertBefore;
  prototype.insertBefore = function (
    this: NodePrototypeLike,
    newChild: unknown,
    referenceChild: ChildNodeLike | null,
  ) {
    if (referenceChild && referenceChild.parentNode !== this) {
      noteForeignMutation("insertBefore");
      // The reference node moved away; keep the new content reachable by
      // appending it rather than dropping it on the floor.
      if (typeof this.appendChild === "function") {
        return this.appendChild(newChild);
      }
      return newChild;
    }
    return originalInsertBefore.call(this, newChild, referenceChild);
  };
};
